export interface BlogPost {
  id: string
  title: string
  excerpt: string
  text: string
  publishedAt: string
  author: string
  readTime: string
}

export interface PaginatedResult {
  posts: BlogPost[]
  total: number
}

const API_BASE = '/api/blog'

export function computeExcerpt(text: string): string {
  const cleaned = text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .replace(/\[([^\]]*)\]\(.*?\)/g, '$1')
    .replace(/(\*{1,3}|_{1,3})(.*?)\1/g, '$2')
    .replace(/`{1,3}[^`]*`{1,3}/g, '')
    .replace(/^>+\s*/gm, '')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/---+/g, '')
    .replace(/\n{2,}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (cleaned.length <= 200) return cleaned
  return cleaned.slice(0, 200).replace(/\s+\S*$/, '') + '...'
}

export function computeReadTime(text: string): string {
  const words = text.trim().split(/\s+/).length
  const minutes = Math.max(1, Math.ceil(words / 200))
  return `${minutes} min read`
}

interface OutlineDoc {
  id: string
  urlId: string
  title: string
  text: string
  publishedAt: string
  createdAt: string
  createdBy: { name: string }
  updatedAt: string
}

function transformOutlineDoc(doc: OutlineDoc): BlogPost {
  return {
    id: doc.urlId,
    title: doc.title,
    excerpt: computeExcerpt(doc.text),
    text: doc.text,
    publishedAt: doc.publishedAt || doc.createdAt,
    author: doc.createdBy?.name || 'greenmango team',
    readTime: computeReadTime(doc.text),
  }
}

export async function getBlogPosts(
  limit: number = 10,
  offset: number = 0
): Promise<PaginatedResult> {
  const response = await fetch(`${API_BASE}/posts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sort: 'publishedAt',
      direction: 'DESC',
      limit,
      offset,
    }),
  })

  if (!response.ok) {
    throw new Error(`Blog API error: ${response.status} ${response.statusText}`)
  }

  const data = await response.json()

  if (!data.ok) {
    throw new Error(`Blog API error: ${data.error} — ${data.message}`)
  }

  return {
    posts: data.data.map(transformOutlineDoc),
    total: data.pagination.total,
  }
}

export async function getBlogPost(urlId: string): Promise<BlogPost | null> {
  const response = await fetch(`${API_BASE}/posts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sort: 'publishedAt',
      direction: 'DESC',
      limit: 100,
    }),
  })

  if (!response.ok) return null

  const data = await response.json()
  if (!data.ok) return null

  const doc = data.data.find((d: OutlineDoc) => d.urlId === urlId)
  return doc ? transformOutlineDoc(doc) : null
}